import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import LearningItemImage from '../../components/LearningItemImage/LearningItemImage'
import CertificateSlug from '../../components/slugs/CertificateSlug/CertificateSlug'
import { LearningItem } from '../../models/LearningItem'
import { UserLearningAssignment } from '../../models/UserLearningAssignment'
import finishedLearningItems from '../../state/calculations/finishedLearningItems'
import learningItemTimeInvested from '../../state/calculations/learningItemTimeInvested'
import { userCanViewPrivateUserInformation } from '../../state/calculations/permissions/userCan'
import { useUser } from '../../state/entities'
import useUserLearningItems from '../../state/queries/useUserLearningItems'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'

import styles from './ManageUserPage.module.scss'
import CourseAssignments from '../../components/CourseAssignments/CourseAssignments'

/**********************************************************************
 * FinishedLearningItem
 **********************************************************************/
const FinishedLearningItem = React.memo(function FinishedLearningItem({
  learningItem,
  assignment,
}: {
  learningItem: LearningItem
  assignment?: UserLearningAssignment
}) {
  const { t } = useTranslation()
  const routes = useRoutes()

  return (
    <section>
      <div className={styles.information}>
        <header>
          <h1>
            <Link to={routes.course.url({ uuid: learningItem.uuid })}>{learningItem.title}</Link>
          </h1>
          <span>{t(`learning item.type.${learningItem.type}`)}</span>
        </header>

        <section className={styles.assignment}>
          {learningItem.userLearningAssignments && learningItem.userLearningAssignments?.length > 0 && (
            <>
              <h1>{t('assignment')}</h1>
              <CourseAssignments learningItem={learningItem} />
            </>
          )}
        </section>

        <section className={styles.certificate}>
          {learningItem.certificate && (
            <>
              <h1>{t('certificate.certificate')}</h1>
              <div>
                <CertificateSlug certificate={learningItem.certificate} />
              </div>
            </>
          )}
        </section>

        <section className={styles.finishedAt}>
          <h1>{t('learning item.finished at')}</h1>
          <div>
            <span>{t('format.DATE', { date: learningItem.learningProgress?.completedAt })}</span>
            {learningItemTimeInvested(learningItem) > 0 && (
              <span>{t('{duration} spent', { duration: { seconds: learningItemTimeInvested(learningItem) } })}</span>
            )}
          </div>
        </section>
      </div>

      <div className={styles.imageAndDueDate}>
        <Link to={routes.course.url({ uuid: learningItem.uuid })}>
          <LearningItemImage learningItem={learningItem} className={styles.coverImage} />
        </Link>
        <div>{assignment?.dueDate && <span>{t('learning item.due date is', { date: assignment.dueDate })}</span>}</div>
      </div>
    </section>
  )
})

/**********************************************************************
 * ManageUserPageAchievements
 **********************************************************************/
export default React.memo(function ManageUserPageAchievements({ uuid }: { uuid: string }) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()

  const { user } = useUser(uuid)

  const { userLearningItems } = useUserLearningItems(uuid)

  const finishedItems = useMemo(() => finishedLearningItems(userLearningItems), [userLearningItems])

  if (!uuid || !user) {
    throw new Error('User not found')
  }

  if (!userCanViewPrivateUserInformation(currentUser, user)) {
    return null
  }

  return (
    <section className={styles.ManageUserPageAchievements}>
      <header>
        <h1>{t('achievements')}</h1>
      </header>

      <main>
        {finishedItems.map(({ learningItem, assignment }) => (
          <FinishedLearningItem key={learningItem.uuid} learningItem={learningItem} assignment={assignment as any} />
        ))}
        {!finishedItems.length && <span className={styles.empty}>{t('no achievements found')}</span>}
      </main>
    </section>
  )
})
